import type { MapRef } from "@vis.gl/react-maplibre";
import { LngLatBounds, type Map as MapLibreMap } from "maplibre-gl";
import type { RefObject } from "react";

import {
  BOUNDS_EQUALITY_EPSILON_DEG,
  DEGENERATE_BOUNDS_SPAN_DEG,
  FIT_PADDING_AXES,
  FIT_PADDING_AXIS_CLEARANCE_PX,
  Log,
} from "@ryde/shared";

import { getAliveMap, isMapLibreReadyToPlacePins } from "./maplibre-map.utils";

/**
 * Whether two bounds are the same view. Guards the re-fit: `fitBounds` on identical bounds still animates and
 * fires move events, so an unchanged poll would fight the user's pan.
 */
export const areLngLatBoundsEqual = (
  a: LngLatBounds | undefined,
  b: LngLatBounds | undefined,
): boolean => {
  if (!a || !b) {
    return a === b;
  }

  const aSw = a.getSouthWest();
  const aNe = a.getNorthEast();
  const bSw = b.getSouthWest();
  const bNe = b.getNorthEast();

  return (
    Math.abs(aSw.lng - bSw.lng) < BOUNDS_EQUALITY_EPSILON_DEG &&
    Math.abs(aSw.lat - bSw.lat) < BOUNDS_EQUALITY_EPSILON_DEG &&
    Math.abs(aNe.lng - bNe.lng) < BOUNDS_EQUALITY_EPSILON_DEG &&
    Math.abs(aNe.lat - bNe.lat) < BOUNDS_EQUALITY_EPSILON_DEG
  );
};

/**
 * Grow bounds by a ratio of their own span, so the fitted routes do not touch the viewport edge. A single
 * point has no span to scale, so it falls back to a fixed degree pad instead of padding by zero.
 */
export const padLngLatBounds = (
  bounds: LngLatBounds,
  padRatio: number,
): LngLatBounds => {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  const latPad =
    (ne.lat - sw.lat) * padRatio || padRatio * DEGENERATE_BOUNDS_SPAN_DEG;
  const lngPad =
    (ne.lng - sw.lng) * padRatio || padRatio * DEGENERATE_BOUNDS_SPAN_DEG;

  return new LngLatBounds(
    [sw.lng - lngPad, sw.lat - latPad],
    [ne.lng + lngPad, ne.lat + latPad],
  );
};

/**
 * Whether the map container has been laid out yet. `fitBounds` on a 0×0 container computes a nonsense zoom,
 * so a fit attempted before layout is deferred instead.
 */
export const hasUsableMapContainer = (
  mapRef: RefObject<MapRef | null>,
): boolean => {
  const container = getAliveMap(mapRef.current)?.getContainer();
  return Boolean(container?.offsetWidth && container?.offsetHeight);
};

/**
 * Padding MapLibre can honour on this canvas. `fitBounds({ padding: 50 })` on a map shorter than 100px
 * produces NaN zoom (grey tiles, inverted Google viewport) and then adding a pin crashes.
 */
export const clampMapLibreFitPadding = (
  map: MapLibreMap,
  padding: number,
): number => {
  const container = map.getContainer();
  const width = container?.offsetWidth ?? 0;
  const height = container?.offsetHeight ?? 0;
  // Shorter axis only: left+right and top+bottom both consume 2× padding, so the limit is half the
  // smaller side minus 1px. On an 80px card that is 39, not the requested 50.
  const maxPadding = Math.max(
    0,
    Math.floor(Math.min(width, height) / FIT_PADDING_AXES) -
      FIT_PADDING_AXIS_CLEARANCE_PX,
  );

  return Math.min(Math.max(0, padding), maxPadding);
};

/** `fitBounds` that will not throw or leave the map on NaN zoom when padding exceeds the canvas. */
export const fitMapLibreBoundsSafely = ({
  map,
  bounds,
  padding,
  duration = 0,
  maxZoom,
}: {
  map: MapLibreMap;
  bounds: LngLatBounds;
  padding: number;
  duration?: number;
  maxZoom?: number;
}): boolean => {
  if (!isMapLibreReadyToPlacePins(map)) {
    return false;
  }

  // Snapshot before the call: a failed fit can leave NaN zoom, and we must jump back to a known-good camera.
  const previousCenter = map.getCenter();
  const previousZoom = map.getZoom();

  try {
    map.fitBounds(bounds, {
      padding: clampMapLibreFitPadding(map, padding),
      duration,
      ...(maxZoom === undefined ? {} : { maxZoom }),
    });
  } catch (error) {
    Log.warn("MapLibre fitBounds exception", error);
    return false;
  }

  if (isMapLibreReadyToPlacePins(map)) {
    return true;
  }

  // Silent NaN (padding vs canvas, 0-scale zoom) must not stick: a parked retry would hit the
  // ready-guard above and never fit again, and pins already on the map would still crash.
  try {
    map.jumpTo({ center: previousCenter, zoom: previousZoom });
  } catch (error) {
    Log.warn("MapLibre camera restore after failed fitBounds", error);
  }

  return false;
};
